import { useEffect, useState, useTransition } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  editFormDataAF,
  EditFormMode,
  IStationRouteDto,
  Log,
} from "@ryde/shared";

import { useRideSaveRouteActions } from "./use-ride-save-route-actions";
import { useRideSaveRoutePath } from "./use-ride-save-route-path";

export const useStationRouteIndicator = () => {
  const { stationRouteId, rideId, rideDto } = useRideSaveRoutePath();
  const [isRouteExists, setIsRouteExists] = useState(true);
  const [isRoutePaused, setIsRoutePaused] = useState(false);
  const [, startTransition] = useTransition();
  const stationRouteDto = useRecoilValue<Partial<IStationRouteDto> | undefined>(
    editFormDataAF(["stationRoute", EditFormMode.UnboundDrawer]),
  );

  const { checkIfStationRouteExist, checkIfStationRoutePaused } =
    useRideSaveRouteActions();

  const checkRouteExistence = useEvent(
    async (routeId: number): Promise<void> => {
      const isRouteExistsApiResult = await checkIfStationRouteExist(routeId);
      setIsRouteExists(isRouteExistsApiResult);
    },
  );

  const checkIsRoutePaused = useEvent(
    async (routeId: number, rideCheckId: number): Promise<void> => {
      const isRoutePausedApiResult = await checkIfStationRoutePaused(
        routeId,
        rideCheckId,
      );
      setIsRoutePaused(isRoutePausedApiResult);
    },
  );

  useEffect(() => {
    Log.debug("Station route in drawer", stationRouteDto);

    if (stationRouteId) {
      startTransition(() => {
        checkRouteExistence(stationRouteId);
      });
    }
  }, [checkRouteExistence, stationRouteDto, stationRouteId]);

  useEffect(() => {
    if (stationRouteId && rideId) {
      const excludedStationRouteIds =
        rideDto?.metadata?.excludedStationRouteIds;
      const isExcluded = excludedStationRouteIds?.length
        ? new Set(excludedStationRouteIds).has(stationRouteId)
        : false;
      Log.debug("Station route in drawer paused", isExcluded);
      startTransition(() => {
        checkIsRoutePaused(stationRouteId, rideId);
      });
    }
  }, [
    checkIsRoutePaused,
    rideDto?.metadata?.excludedStationRouteIds,
    rideId,
    stationRouteId,
  ]);

  return { isRouteExists, isRoutePaused };
};
